import { useStore } from "@nanostores/preact"
import { useEffect, useRef, useState } from "preact/hooks"
import { loadStripe } from "@stripe/stripe-js/pure"
import { Stripe, PaymentRequest } from "@stripe/stripe-js"
import {
  Elements,
  PaymentRequestButtonElement,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js"
import classNames from "utils/preact/class-names"

import {
  SESSION_RECORDING_EVENTS,
  sendSessionRecordingEvent,
  sendSubscriptionFunnelEvent,
} from "utils/session-recording"
import { pushOnce } from "utils/data-layer"
import { trackExperimentExposure } from "utils/experiment-analytics"
import { Currency, IsoCountry, Price, Products, StripeCheckoutVariant } from "../services/support"
import { profile, promo } from "../services/store"
import { useTranslation } from "../components/Translation"
import { PromoCodeSection } from "../components/PromoCodeSection"
import { PriceDisplay } from "../components/PriceDisplay"
import { applyPromoToAmount, monthlyEquivalentAmount, promoIsApplied } from "../services/promo"
import {
  STRIPE_CHECKOUT_EXPERIMENT_ID,
  trackAddPaymentInfo,
  trackBeginCheckout,
  trackPurchase,
} from "../services/analytics"

import css from "../style.module.scss"
import cards from "../static/cards.png"
import { SubscriptionButtons } from "../components/SubscriptionButtons"
import { Sidebar } from "../components/Sidebar"

function pay(url: string, token: string, body: string): Promise<undefined | Record<string, any>> {
  return fetch(url, {
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": token,
    },
    credentials: "include",
    body,
  })
    .then((res) => {
      if (!res.ok && res.status !== 422 && res.status !== 401) {
        return {
          base: `${res.status}: ${res.statusText}`,
        }
      }

      return res.json()
    })
    .then((res) => {
      if (res.success) return undefined
      if (res.errors) return res.errors
      return res
    })
    .catch((err: Error) => ({
      base: err.message,
    }))
}

// The URL of the page the visitor should come back to from the Stripe-hosted
// Checkout page, stripped of any previous return markers.
function checkoutReturnUrl(): string {
  const params = new URLSearchParams(window.location.search)
  params.delete("checkout")
  params.delete("session_id")
  const query = params.toString()
  const { origin, pathname } = window.location
  return `${origin}${pathname}${query ? `?${query}` : ""}`
}

// The "checkout" experiment arm: no card fields, just a submit that creates a
// Stripe Checkout Session and redirects to its Stripe-hosted payment page.
function CheckoutForm({
  price,
  token,
  features,
}: {
  price: Price
  token: string
  features?: { promo_code_field: boolean }
}) {
  const user = useStore(profile)
  const promoState = useStore(promo)
  const t = useTranslation()

  const [isLoading, setIsLoading] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const promoValidationValid = promoIsApplied(promoState.validation)
  const discountedAmount = applyPromoToAmount(
    price.unit_amount,
    price.currency,
    promoState.validation,
  )

  const handleSubmit = async (event: Event) => {
    event.preventDefault()

    sendSubscriptionFunnelEvent(SESSION_RECORDING_EVENTS.subCheckoutStarted)
    setIsLoading(true)
    setSubmitError("")

    const response = await pay(
      "/sign_up/stripe_payment/checkout_session",
      token,
      JSON.stringify({
        stripe_payment: {
          price_id: price.price_id,
          promo_code: promoValidationValid ? promoState.code : undefined,
          return_url: checkoutReturnUrl(),
        },
      }),
    )

    if (response && response.url) {
      trackAddPaymentInfo(
        user.subscription === "free" ? "annually" : user.subscription,
        price,
        discountedAmount,
        "stripe_checkout",
        promoValidationValid ? promoState.code : undefined,
      )
      // Keep the loading state while the browser navigates to Stripe.
      window.location.assign(response.url as string)
      return
    }

    if (response && response.base) setSubmitError(response.base)
    else setSubmitError("Something went wrong")

    setIsLoading(false)
  }

  return (
    <form action="/sign_up/stripe_payment/checkout_session" method="post" onSubmit={handleSubmit}>
      <img
        src={cards}
        alt="Payment providers"
        width={217}
        height={20}
        className={css["spacer-bottom"]}
      />
      {features?.promo_code_field && (
        <>
          <PromoCodeSection token={token} />
          <div className={css["spacer-bottom"]} />
        </>
      )}
      {submitError && (
        <p
          className={classNames({
            [css["text-medium"]]: true,
            [css["text-center"]]: true,
            [css["error-highlight"]]: true,
          })}
        >
          {submitError}
        </p>
      )}
      <button
        className={classNames({
          [css.submit]: true,
          [css["is-loading"]]: isLoading,
        })}
        type="submit"
      >
        {t("pay_now")}
      </button>
    </form>
  )
}

function PaymentForm({
  price,
  country,
  currency,
  token,
  features,
  onPay,
}: {
  price: Price
  country: IsoCountry
  currency: Currency
  token: string
  features?: { promo_code_field: boolean }
  onPay: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const user = useStore(profile)
  const promoState = useStore(promo)
  const t = useTranslation()

  const [isLoading, setIsLoading] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const promoValidationValid = promoIsApplied(promoState.validation)
  const discountedAmount = applyPromoToAmount(
    price.unit_amount,
    price.currency,
    promoState.validation,
  )

  useEffect(() => {
    if (stripe) {
      ;(async () => {
        const pr = stripe.paymentRequest({
          country: country.iso.toUpperCase(),
          currency: currency.acronym.toLowerCase(),
          total: {
            label: `Support ${user.subscription}`,
            amount: discountedAmount,
          },
          requestPayerName: true,
          requestPayerEmail: true,
        })
        const canMakePayment = await pr.canMakePayment()
        if (canMakePayment) {
          setPaymentRequest(pr)
        }
      })()
    }
  }, [stripe, country, currency, discountedAmount, user.subscription])

  useEffect(() => {
    if (paymentRequest) {
      paymentRequest.on("paymentmethod", async (paymentResponse) => {
        sendSubscriptionFunnelEvent(SESSION_RECORDING_EVENTS.subCheckoutStarted)
        setIsLoading(true)
        setSubmitError("")

        const result = await pay(
          "/sign_up/stripe_payment",
          token,
          JSON.stringify({
            stripe_payment: {
              price_id: price.price_id,
              promo_code: promoValidationValid ? promoState.code : undefined,
            },
            source: user.source,
          }),
        )

        if (result && result.clientSecret) {
          trackAddPaymentInfo(
            user.subscription === "free" ? "annually" : user.subscription,
            price,
            discountedAmount,
            "payment_request",
            promoValidationValid ? promoState.code : undefined,
          )

          const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
            result.clientSecret,
            { payment_method: paymentResponse.paymentMethod.id },
            { handleActions: false },
          )

          if (confirmError) {
            // eslint-disable-next-line no-console
            console.error(confirmError)

            setIsLoading(false)
            setSubmitError(confirmError.message)
            paymentResponse.complete("fail")
            return
          }

          paymentResponse.complete("success")

          if (paymentIntent.status === "requires_action") {
            const { error } = await stripe.confirmCardPayment(result.clientSecret)
            if (error) {
              // eslint-disable-next-line no-console
              console.error(error)

              setIsLoading(false)
              setSubmitError(error.message)
              return
            }
          }

          profile.setKey("premium", true)
          profile.setKey("reloadRequired", true)
          trackPurchase(
            result.subscriptionId,
            user.subscription === "free" ? "annually" : user.subscription,
            price,
            discountedAmount,
            promoValidationValid ? promoState.code : undefined,
          )
          onPay()
        } else if (result && result.base) setSubmitError(result.base)
        else setSubmitError("Something went wrong")

        setIsLoading(false)
      })
    }

    return () => {
      if (paymentRequest) paymentRequest.off("paymentmethod")
    }
  }, [
    stripe,
    paymentRequest,
    token,
    price,
    onPay,
    user,
    promoState.code,
    promoValidationValid,
    discountedAmount,
  ])

  const handleSubmit = async (event: Event) => {
    event.preventDefault()

    sendSubscriptionFunnelEvent(SESSION_RECORDING_EVENTS.subCheckoutStarted)
    setIsLoading(true)
    setSubmitError("")

    const response = await pay(
      "/sign_up/stripe_payment",
      token,
      JSON.stringify({
        stripe_payment: {
          price_id: price.price_id,
          promo_code: promoValidationValid ? promoState.code : undefined,
        },
        source: user.source,
      }),
    )

    if (response && response.clientSecret) {
      trackAddPaymentInfo(
        user.subscription === "free" ? "annually" : user.subscription,
        price,
        discountedAmount,
        "card",
        promoValidationValid ? promoState.code : undefined,
      )

      const { paymentIntent, error } = await stripe.confirmCardPayment(response.clientSecret, {
        payment_method: {
          card: elements.getElement("card"),
        },
      })

      if (error) {
        // eslint-disable-next-line no-console
        console.error("failed while paying using filled in card details", error)
        setSubmitError(error.message)
      } else if (paymentIntent.status === "succeeded") {
        profile.setKey("premium", true)
        profile.setKey("reloadRequired", true)
        trackPurchase(
          response.subscriptionId,
          user.subscription === "free" ? "annually" : user.subscription,
          price,
          discountedAmount,
          promoValidationValid ? promoState.code : undefined,
        )
        onPay()
      } else {
        setSubmitError(`Something went wrong.\n(paymentIntent.status is ${paymentIntent.status})`)
      }
    } else if (response && response.base) {
      setSubmitError(response.base)
    } else {
      setSubmitError("Something went wrong")
    }

    setIsLoading(false)
  }

  return (
    <form action="/sign_up/stripe_payment" method="post" onSubmit={handleSubmit}>
      {paymentRequest && (
        <div className={css["spacer-bottom"]}>
          <PaymentRequestButtonElement options={{ paymentRequest }} />
        </div>
      )}
      <img
        src={cards}
        alt="Payment providers"
        width={217}
        height={20}
        className={css["spacer-bottom"]}
      />
      <div className={css["card-wrapper"]}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                lineHeight: "1.5",
              },
            },
          }}
        />
      </div>
      <div className={css["spacer-bottom"]} />
      {features?.promo_code_field && (
        <>
          <PromoCodeSection token={token} />
          <div className={css["spacer-bottom"]} />
        </>
      )}
      {submitError && (
        <p
          className={classNames({
            [css["text-medium"]]: true,
            [css["text-center"]]: true,
            [css["error-highlight"]]: true,
          })}
        >
          {submitError}
        </p>
      )}
      <button
        className={classNames({
          [css.submit]: true,
          [css["is-loading"]]: isLoading,
        })}
        type="submit"
      >
        {t("pay_now")}
      </button>
    </form>
  )
}

export function Payment({
  token,
  products,
  currencies,
  isoCountries,
  publishableKey,
  features,
  stripeCheckoutVariant,
  onPay,
}: {
  token: string
  products: Products
  currencies: Currency[]
  isoCountries: IsoCountry[]
  publishableKey: string
  features?: { promo_code_field: boolean }
  stripeCheckoutVariant?: StripeCheckoutVariant
  onPay: () => void
}) {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const beginCheckoutTracked = useRef(false)
  const checkoutArm = stripeCheckoutVariant === "checkout"

  useEffect(() => {
    if (checkoutArm) return // the Stripe-hosted page brings its own Stripe.js
    ;(async () => {
      const resolved = await loadStripe(publishableKey)
      setStripe(resolved)
    })()
  }, [publishableKey, checkoutArm])

  const t = useTranslation()
  const user = useStore(profile)
  const subscription = user.subscription === "free" ? "annually" : user.subscription
  const country = isoCountries.find((c) => c.iso === user.countryIso)
  const currency = currencies.find((c) => c.acronym === country.currency)
  const price = products[subscription].prices[currency.acronym]
  const promoState = useStore(promo)
  const discountedAmount = applyPromoToAmount(
    price.unit_amount,
    price.currency,
    promoState.validation,
  )
  const originalMonthlyEquivalent = monthlyEquivalentAmount(price.unit_amount, subscription)
  const discountedMonthlyEquivalent = monthlyEquivalentAmount(discountedAmount, subscription)

  useEffect(() => {
    if (beginCheckoutTracked.current) return

    // The experiment arm event goes first so the per-arm Hotjar funnels see
    // it before the shared funnel steps of the same pageview.
    if (stripeCheckoutVariant) {
      sendSessionRecordingEvent(
        checkoutArm
          ? SESSION_RECORDING_EVENTS.subPayVariantCheckout
          : SESSION_RECORDING_EVENTS.subPayVariantElements,
      )
    }
    sendSubscriptionFunnelEvent(SESSION_RECORDING_EVENTS.subConfirmationViewed)
    trackBeginCheckout(subscription, price, discountedAmount, promoState.code || undefined)
    if (stripeCheckoutVariant) {
      pushOnce(`experiment_exposure:${STRIPE_CHECKOUT_EXPERIMENT_ID}`, () =>
        trackExperimentExposure({
          experimentId: STRIPE_CHECKOUT_EXPERIMENT_ID,
          experimentVariant: stripeCheckoutVariant,
        }),
      )
    }
    beginCheckoutTracked.current = true
  }, [checkoutArm, discountedAmount, price, promoState.code, stripeCheckoutVariant, subscription])

  return (
    <div className={css.content}>
      <div className={css["split-container"]}>
        <div className={css["flex-grow"]}>
          <Sidebar />
          <SubscriptionButtons />
        </div>
        <div className={css["horizontal-divider"]} />
        <div className={css["flex-grow"]}>
          <h3 className={css["form-title"]}>{t("payment_information")}</h3>
          <p className={css["account-text"]}>
            {t("account")}
            {": "}
            {user.email || profile.get().email}
          </p>

          <div className={css["payment-info"]}>
            {subscription === "monthly" && (
              <p className={css["payment-membership"]}>
                {t("monthly")} {t("membership")}
              </p>
            )}
            {subscription === "annually" && (
              <p className={css["payment-membership"]}>
                {t("annual")} {t("membership")}
              </p>
            )}
            <p className={css["payment-amount"]}>
              <PriceDisplay
                originalAmount={price.unit_amount}
                discountedAmount={discountedAmount}
                currency={currency}
              />
            </p>
          </div>
          {subscription === "monthly" && (
            <p className={css["payment-billing"]}>
              {t("billed_at") || "Billed at"}{" "}
              <PriceDisplay
                originalAmount={price.unit_amount}
                discountedAmount={discountedAmount}
                currency={currency}
              />
              {t("per_month") || "/month"}
            </p>
          )}
          {subscription === "annually" && (
            <p className={css["payment-billing"]}>
              {t("billed_at") || "Billed at"}{" "}
              <PriceDisplay
                originalAmount={price.unit_amount}
                discountedAmount={discountedAmount}
                currency={currency}
              />
              {t("per_year") || "/year"}, {t("just") || "just"}{" "}
              <PriceDisplay
                originalAmount={originalMonthlyEquivalent}
                discountedAmount={discountedMonthlyEquivalent}
                currency={currency}
              />
              {t("per_month") || "/month"}
            </p>
          )}
          <div className={css["payment-form"]}>
            {checkoutArm && <CheckoutForm token={token} price={price} features={features} />}
            {!checkoutArm && stripe && (
              <Elements stripe={stripe}>
                <PaymentForm
                  token={token}
                  price={price}
                  country={country}
                  currency={currency}
                  features={features}
                  onPay={onPay}
                />
              </Elements>
            )}
            {!checkoutArm && !stripe && (
              <div className={css["text-center"]}>
                <div className={css.loader} />
                <div className={css["text-medium"]}>Loading payment module...</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
